import { ApplicationConfig, importProvidersFrom, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Title } from '@angular/platform-browser';

import { NgxsModule } from '@ngxs/store';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';

import { provideToastr } from 'ngx-toastr';
import { ModalModule } from 'ngx-bootstrap/modal';
import { provideEchartsCore } from 'ngx-echarts';

import { GenericToastComponent } from '@shared/component';

import { provideBaseUrl, providePageTitleStrategy, provideStateLogoutPlugin } from '@core/provider';
import { baseHttpInterceptor } from '@core/interceptor';
import {
    AuthState,
    CategoryManagementState,
    CompanyManagementState,
    ConfigurationsState,
    ContactMessageState,
    DashboardState,
    IdeasOccasionsManagementState,
    MyVerificationState,
    NewsletterSubscriberState,
    ProductsState,
    QuoteDetailState,
    QuotesState,
    SupplierVerificationManagementState,
    UserManagementState,
} from '@data/state';
import { isDateExpired } from '@shared/util';

import { environment } from '@env';
import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
    providers: [
        Title,
        provideBrowserGlobalErrorListeners(),
        provideRouter(
            routes,
            withComponentInputBinding(),
            withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' }),
        ),
        provideHttpClient(withInterceptors([baseHttpInterceptor])),
        provideAnimations(),
        provideToastr({
            toastComponent: GenericToastComponent,
            positionClass: 'toast-bottom-right',
            progressBar: true,
            closeButton: true,
            timeOut: 4000,
            extendedTimeOut: 2000,
        }),
        provideBaseUrl(),
        providePageTitleStrategy(),
        provideStateLogoutPlugin(),
        provideEchartsCore({ echarts: () => import('echarts') }),
        importProvidersFrom(
            NgxsModule.forRoot(
                [
                    AuthState,
                    CategoryManagementState,
                    CompanyManagementState,
                    ConfigurationsState,
                    ContactMessageState,
                    DashboardState,
                    IdeasOccasionsManagementState,
                    MyVerificationState,
                    NewsletterSubscriberState,
                    ProductsState,
                    QuoteDetailState,
                    QuotesState,
                    SupplierVerificationManagementState,
                    UserManagementState,
                ],
                {
                    developmentMode: !environment.production,
                    selectorOptions: {
                        suppressErrors: false,
                        injectContainerState: false,
                    },
                },
            ),
            NgxsStoragePluginModule.forRoot({
                keys: [AuthState],
                afterDeserialize: (obj, key) => {
                    if (key === 'auth' && obj?.token && isDateExpired(obj.refreshTokenExpiration)) {
                        return null;
                    }
                    return obj;
                },
            }),
            NgxsLoggerPluginModule.forRoot({ disabled: environment.production }),
            NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production }),
            ModalModule,
        ),
    ],
};
